import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ImagePlus, Loader2, X, Gift, Lock, Users } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StepIndicator } from "@/components/StepIndicator";
import { useBooking } from "@/context/BookingContext";
import { createBooking, createCheckout } from "@/lib/api";
import { COLLECTIONS, SIZES, humanSlot, paletteSummary, addonsSummary, estimateTotal, PAYMENT_NOTE } from "@/data/collections";
import { toast } from "sonner";

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function Contact() {
  const [sp] = useSearchParams();
  useEffect(() => { if (sp.get("cancelled")) toast("Checkout cancelled — your details are still here whenever you're ready."); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const nav = useNavigate();
  const { booking, update } = useBooking();
  const [submitting, setSubmitting] = useState(false);
  const collection = COLLECTIONS.find((c) => c.id === booking.collectionId);
  const size = SIZES.find((s) => s.id === booking.size);

  useEffect(() => {
    if (!booking.collectionId || !booking.date || !booking.timeSlot) nav("/", { replace: true });
  }, [booking, nav]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Please upload a photo under 4 MB");
    update({ photoBase64: await readAsBase64(f) });
  };

  const submit = async (e) => {
    e.preventDefault();
    const { firstName, lastName, email, phone, street, city, state, zip, isGift, recipientName } = booking;
    if (!firstName || !lastName || !email || !phone || !street || !city || !state || !zip) return toast.error("Please fill in all contact details");
    if (isGift && !recipientName) return toast.error("Please tell us who the gift is for");
    setSubmitting(true);
    try {
      const created = await createBooking({
        collection_id: booking.collectionId,
        size: booking.size,
        addons: booking.addons,
        custom_colors: {},
        palette: booking.collectionId === "build-your-own" ? booking.palette : null,
        extra_pumpkins: booking.collectionId === "build-your-own" && booking.size === "estate" ? booking.extraPumpkins || 0 : 0,
        mum_colors: booking.mumColors,
        mum_color_other: booking.mumColorOther,
        customer_notes: booking.customerNotes,
        referred_by: booking.referredBy,
        priority_removal: booking.priorityRemoval,
        removal_date: booking.priorityRemoval ? booking.removalDate : null,
        date: booking.date,
        time_slot: booking.timeSlot,
        first_name: firstName, last_name: lastName,
        email, phone, street, city, state, zip,
        photo_base64: booking.photoBase64,
        photo_consent: booking.photoConsent,
        is_gift: isGift,
        recipient_name: isGift ? recipientName : null,
        gift_message: isGift ? booking.giftMessage : null,
      });
      const { url } = await createCheckout(created.id);
      toast.success("Taking you to secure checkout…");
      window.location.href = url;
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || "Something went wrong. Please try again."));
      setSubmitting(false);
    }
  };

  const palette = booking.collectionId === "build-your-own" ? paletteSummary(booking.palette) : "";
  const est = estimateTotal(booking.collectionId, booking.size, booking.addons, booking.priorityRemoval, booking.extraPumpkins);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <StepIndicator current={4} />

      <section className="pp-container pb-20 pt-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <form className="lg:col-span-7 space-y-8" onSubmit={submit}>
            <div>
              <div className="pp-caption mb-3">Step four · Your details</div>
              <h1 className="font-serif-display text-4xl sm:text-5xl font-light" style={{ color: "var(--pp-burgundy)" }}>
                A few details, and we'll reserve this for you
              </h1>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="First name"><input data-testid="contact-first-name-input" required value={booking.firstName} onChange={(e) => update({ firstName: e.target.value })} className={inputCls} /></Field>
              <Field label="Last name"><input data-testid="contact-last-name-input" required value={booking.lastName} onChange={(e) => update({ lastName: e.target.value })} className={inputCls} /></Field>
              <Field label="Email"><input data-testid="contact-email-input" type="email" required value={booking.email} onChange={(e) => update({ email: e.target.value })} className={inputCls} /></Field>
              <Field label="Phone"><input data-testid="contact-phone-input" type="tel" required value={booking.phone} onChange={(e) => update({ phone: e.target.value })} className={inputCls} /></Field>
              <div className="sm:col-span-2"><Field label={booking.isGift ? "Recipient's street address" : "Street address"}><input data-testid="contact-street-input" required value={booking.street} onChange={(e) => update({ street: e.target.value })} className={inputCls} /></Field></div>
              <Field label="City"><input data-testid="contact-city-input" required value={booking.city} onChange={(e) => update({ city: e.target.value })} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-6">
                <Field label="State"><input data-testid="contact-state-input" required maxLength={2} value={booking.state} onChange={(e) => update({ state: e.target.value.toUpperCase() })} placeholder="UT" className={inputCls} /></Field>
                <Field label="Zip code"><input data-testid="contact-zip-input" required inputMode="numeric" value={booking.zip} onChange={(e) => update({ zip: e.target.value })} className={inputCls} /></Field>
              </div>
            </div>

            <div className="border p-5" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }} data-testid="gift-section">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" data-testid="gift-toggle" checked={booking.isGift} onChange={(e) => update({ isGift: e.target.checked })} className="mt-1 h-4 w-4 accent-[var(--pp-terracotta)]" />
                <span>
                  <span className="flex items-center gap-2 font-medium" style={{ color: "var(--pp-burgundy)" }}><Gift className="h-4 w-4" /> This porch is a gift</span>
                  <span className="block text-sm mt-0.5" style={{ color: "var(--pp-text-muted)" }}>Surprise a friend or neighbor. We'll quote and arrange payment with you, and style their porch.</span>
                </span>
              </label>
              {booking.isGift && (
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Recipient's name"><input data-testid="gift-recipient-input" value={booking.recipientName} onChange={(e) => update({ recipientName: e.target.value })} className={inputCls} /></Field>
                  <Field label="Gift message (optional)"><input data-testid="gift-message-input" value={booking.giftMessage} onChange={(e) => update({ giftMessage: e.target.value })} placeholder="Happy autumn, from the Hudsons!" className={inputCls} /></Field>
                </div>
              )}
            </div>

            <div className="border p-5" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }} data-testid="referral-section">
              <div className="flex items-center gap-2 font-medium" style={{ color: "var(--pp-burgundy)" }}><Users className="h-4 w-4" /> Were you referred by a friend?</div>
              <p className="text-sm mt-0.5 mb-4" style={{ color: "var(--pp-text-muted)" }}>Tell us who sent you and you'll <strong>both receive $50 back</strong> after your install.</p>
              <Field label="Referrer's name (optional)"><input data-testid="referred-by-input" value={booking.referredBy} onChange={(e) => update({ referredBy: e.target.value })} placeholder="e.g. Sarah Hudson" className={inputCls} /></Field>
            </div>

            <Field label="Porch photo (optional)">
              <p className="text-sm mb-3" style={{ color: "var(--pp-text-muted)" }}>Upload a photo of your porch or front entry (optional, but helps us plan faster and confirm your quote sooner).</p>
              {booking.photoBase64 ? (
                <div className="relative inline-block">
                  <img src={booking.photoBase64} alt="Porch preview" className="h-40 w-auto border object-cover" style={{ borderColor: "var(--pp-border)" }} />
                  <button type="button" onClick={() => update({ photoBase64: null })} aria-label="Remove photo"
                    className="absolute -top-2 -right-2 h-7 w-7 inline-flex items-center justify-center rounded-full text-white" style={{ backgroundColor: "var(--pp-burgundy)" }}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label htmlFor="porch-photo" className="inline-flex items-center gap-2 border border-dashed px-5 py-4 cursor-pointer hover:bg-[var(--pp-cream-card)]" style={{ borderColor: "var(--pp-brown)", color: "var(--pp-brown)" }}>
                  <ImagePlus className="h-5 w-5" /><span className="text-sm">Choose a photo of your porch or front entry</span>
                </label>
              )}
              <input id="porch-photo" data-testid="porch-photo-upload-input" type="file" accept="image/*" className="hidden" onChange={onFile} />
            </Field>

            <label className="flex items-start gap-3 cursor-pointer text-sm" data-testid="photo-consent">
              <input type="checkbox" data-testid="photo-consent-toggle" checked={booking.photoConsent} onChange={(e) => update({ photoConsent: e.target.checked })} className="mt-1 h-4 w-4 accent-[var(--pp-terracotta)]" />
              <span style={{ color: "var(--pp-text-muted)" }}>I'm happy for Pumpkin &amp; Porch to share before and after photos of my install on social media (optional).</span>
            </label>

            <div className="border-l-2 pl-4 text-xs leading-relaxed" style={{ borderColor: "var(--pp-terracotta)", color: "var(--pp-text-muted)" }} data-testid="deposit-note">
              {PAYMENT_NOTE}
            </div>

            <div className="text-xs leading-relaxed border p-3" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)", color: "var(--pp-text-muted)" }} data-testid="changes-note">
              <span className="font-medium" style={{ color: "var(--pp-burgundy)" }}>A note on changes:</span> once your order is confirmed, any changes to colors, pieces or package — for example switching your mums from pink to yellow the day before — are treated as an addition and carry an extra charge.
            </div>

            <button type="submit" data-testid="booking-confirm-submit-button" disabled={submitting} className="pp-btn">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}{!submitting && <Lock className="h-4 w-4" />} Pay ${est.total.toLocaleString()} &amp; book my date
            </button>
          </form>

          <aside className="lg:col-span-5 p-8 h-max lg:sticky lg:top-28 pp-dark" data-testid="booking-review">
            <div className="pp-caption mb-2">Held for you</div>
            <h2 className="font-serif-display text-3xl mb-5" style={{ color: "var(--pp-parchment)" }}>Your porch, so far</h2>
            <dl className="space-y-2.5 text-sm">
              <Row k="Collection" v={collection?.name} />
              <Row k="Package" v={collection?.id === "build-your-own" ? "Build Your Own" : size ? `${size.label} · $${est.tier.toLocaleString()}` : "—"} />
              <Row k="Date" v={booking.date ? new Date(booking.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric" }) : "—"} />
              <Row k="Time" v={booking.timeSlot ? humanSlot(booking.timeSlot) : "—"} />
              {palette && <Row k="Palette" v={palette} />}
              <Row k="Add-ons" v={addonsSummary({ addons: booking.addons, mum_colors: booking.mumColors, mum_color_other: booking.mumColorOther, priority_removal: booking.priorityRemoval })} />
              {booking.customerNotes && <Row k="Notes" v={booking.customerNotes} />}
              {booking.referredBy && <Row k="Referred by" v={booking.referredBy} />}
              <Row k="Total due today" v={`$${est.total.toLocaleString()}`} />
              {booking.isGift && <Row k="Gift for" v={booking.recipientName || "—"} />}
            </dl>
            <p className="mt-6 text-xs leading-relaxed" style={{ color: "#B9A48C" }}>
              You'll be taken to Stripe's secure checkout to pay in full. The moment payment clears, your date is booked and a confirmation lands in your inbox.
            </p>
          </aside>
        </div>
      </section>
      <div className="flex-1" />
      <Footer />
    </div>
  );
}

const inputCls = "w-full border-b bg-transparent px-0 py-3 text-base outline-none focus:border-[var(--pp-terracotta)] transition-colors border-[var(--pp-brown)]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="pp-caption block mb-1">{label}</span>
    {children}
  </label>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 border-b pb-2" style={{ borderColor: "rgba(233,216,190,0.2)" }}>
    <dt style={{ color: "#C9B8A3" }}>{k}</dt>
    <dd className="text-right" style={{ color: "var(--pp-parchment)" }}>{v || "—"}</dd>
  </div>
);
